import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtMoney, EmptyState, Skeleton } from "@/components/Shared";
import PageShell from "@/components/PageShell";
import { EnvelopeSimple, CheckCircle, ArrowsClockwise, Pulse, Warning, ArrowUUpLeft, Lightning } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const KIND_LABEL = {
  invoice_created: "Factura creada",
  reminder_before: "Recordatorio (3 días antes)",
  due_today: "Vence hoy",
  overdue: "Factura vencida",
  manual_resend: "Envío manual",
};

export default function ActivityPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/activities?limit=200");
      setItems(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = items.filter((a) => {
    if (filter === "all") return true;
    if (filter === "payments") return a.type === "payment_received";
    if (filter === "emails") return a.type === "email_sent";
    if (filter === "fraud") return a.type === "fraud_alert";
    if (filter === "refunds") return a.type === "refund_issued";
    if (filter === "disconnections") return a.type === "disconnection_notice";
    return true;
  });

  return (
    <PageShell
      section="Sistema"
      page="Actividad"
      context={`${items.length} eventos`}
      testid="activity-toolbar"
      actions={
        <Button onClick={load} variant="outline" size="sm" data-testid="reload-activity">
          <ArrowsClockwise size={14} className="mr-1.5" /> Actualizar
        </Button>
      }
    >

      <div className="mb-6 flex items-center gap-1 bg-white border border-slate-200 rounded-md p-1 w-fit" data-testid="activity-filter">
        {[
          { v: "all", label: "Todo" },
          { v: "payments", label: "Pagos" },
          { v: "fraud", label: "Fraude" },
          { v: "refunds", label: "Reembolsos" },
          { v: "disconnections", label: "Desconexiones" },
          { v: "emails", label: "Emails" },
        ].map((opt) => (
          <button
            key={opt.v}
            onClick={() => setFilter(opt.v)}
            data-testid={`filter-${opt.v}`}
            className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
              filter === opt.v ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
            }`}
          >{opt.label}</button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-md">
        {loading ? (
          <ul className="divide-y divide-slate-100">
            {Array.from({ length: 6 }).map((_, i) => (
              <li key={i} className="px-5 py-4 flex items-start gap-3">
                <Skeleton className="w-9 h-9 rounded shrink-0" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-2/3" />
                  <Skeleton className="h-3 w-1/4" />
                </div>
              </li>
            ))}
          </ul>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={Pulse}
            title="Sin actividad para mostrar"
            message="Las acciones automáticas (pagos recibidos, emails enviados a clientes) aparecerán aquí en tiempo real."
          />
        ) : (
          <ul className="divide-y divide-slate-100">
            {filtered.map((a, idx) => (
              <li key={a.id || `${a.type}-${a.created_at}-${idx}`} className="px-5 py-4 flex items-start gap-3" data-testid={`activity-item-${idx}`}>
                <div className={`w-9 h-9 rounded shrink-0 flex items-center justify-center ${
                  a.type === "payment_received" ? "bg-emerald-50 text-emerald-700"
                  : a.type === "fraud_alert" ? "bg-red-50 text-red-700"
                  : a.type === "refund_issued" ? "bg-rose-50 text-rose-700"
                  : a.type === "disconnection_notice" ? "bg-red-50 text-red-700"
                  : "bg-blue-50 text-blue-700"
                }`}>
                  {a.type === "payment_received" ? <CheckCircle size={18} weight="duotone" />
                    : a.type === "fraud_alert" ? <Warning size={18} weight="fill" />
                    : a.type === "refund_issued" ? <ArrowUUpLeft size={18} weight="duotone" />
                    : a.type === "disconnection_notice" ? <Lightning size={18} weight="fill" />
                    : <EnvelopeSimple size={18} weight="duotone" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-900">
                    {a.type === "payment_received" && (
                      <>
                        <span className="font-semibold">{a.client_name}</span> pagó{" "}
                        <span className="font-mono font-semibold">{fmtMoney(a.amount)}</span>
                        {a.square_payment_id && (
                          <span className="ml-2 text-[10px] font-mono text-slate-400">Square ID: {a.square_payment_id}</span>
                        )}
                      </>
                    )}
                    {a.type === "email_sent" && (
                      <>
                        Email a <span className="font-semibold">{a.client_name}</span>
                        <span className="text-slate-500"> · {KIND_LABEL[a.kind] || a.kind}</span>
                        {a.amount && <span className="text-slate-500 font-mono"> · {fmtMoney(a.amount)}</span>}
                        {a.mocked && (
                          <span className="ml-2 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-200">MOCK</span>
                        )}
                      </>
                    )}
                    {a.type === "fraud_alert" && (
                      <>
                        <span className="font-semibold text-red-700">Alerta de fraude</span>
                        <span className="text-slate-500"> · {a.client_name}</span>
                        <div className="text-xs text-slate-600 mt-1">{a.description}</div>
                        <span className={`mt-1.5 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider border ${
                          a.severity === "high" ? "bg-red-50 text-red-700 border-red-200" : "bg-amber-50 text-amber-700 border-amber-200"
                        }`}>{a.severity || a.kind}</span>
                      </>
                    )}
                    {a.type === "refund_issued" && (
                      <>
                        Reembolso a <span className="font-semibold">{a.client_name}</span> · <span className="font-mono font-semibold">{fmtMoney(a.amount)}</span>
                        <span className="text-slate-500"> · {a.full_refund ? "Completo" : "Parcial"}</span>
                        {a.reason && <div className="text-xs text-slate-500 mt-1">"{a.reason}"</div>}
                      </>
                    )}
                    {a.type === "disconnection_notice" && (
                      <>
                        <span className="font-semibold text-red-700">Aviso de desconexión enviado</span> a {a.client_name}
                        <div className="text-xs text-slate-600 mt-1">
                          {a.days_overdue}d vencida · Total con reconexión: <span className="font-mono font-semibold">{fmtMoney(a.amount)}</span>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1 font-mono">
                    {new Date(a.created_at).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" })}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </PageShell>
  );
}
